import { useEffect, useState } from "react"
import { clearAuthData } from "@/helpers/clearAuthData"
import { isTokenValid } from "@/helpers/jwt"
import { parseAxiosError } from "@/helpers/parseAxiosError"
import { getTgUserId } from "@/helpers/tgWebApp"
import { useCheckInGame } from "@/hooks/useCheckInGame"
import { useAppDispatch } from "@/store"
import {
  useAuthMutation,
  useRefreshMutation
} from "@/store/apis/authAPI/auth.api"
import { getMaintenance } from "@/store/apis/maintenanceAPI"
import { getPlayerProfile, getPlayerSettings } from "@/store/apis/playerAPI"
import { fetchServerTime } from "@/store/apis/serverTimeAPI"
import { setIsLoaded, setIsLabrUser } from "@/store/slices/app"
import { setIsAuthorized } from "@/store/slices/player"

export const useLoad = () => {
  useCheckInGame()

  const dispatch = useAppDispatch()
  const [progress, setProgress] = useState<number>(0)

  const [auth] = useAuthMutation()
  const [refresh] = useRefreshMutation()

  const updateProgressOnResolve = (
    promise: Promise<any>,
    increment: number
  ) => {
    return promise.finally(() => {
      setProgress((prev) => Math.min(100, +(prev + increment).toFixed()))
    })
  }

  const authUser = async (isValidToken: boolean = false) => {
    if (getTgUserId() || isValidToken) {
      await auth().unwrap()
      return true
    } else {
      clearAuthData()
      dispatch(setIsAuthorized(false))
      return false
    }
  }

  const bootstrap = async () => {
    try {
      if (isTokenValid("accessToken")) {
        dispatch(setIsAuthorized(true))
      } else if (isTokenValid("refreshToken")) {
        try {
          await refresh().unwrap()
        } catch {
          const isAuthSuccess = await authUser(true)
          if (!isAuthSuccess) return
        }
      } else {
        const isAuthSuccess = await authUser()
        if (!isAuthSuccess) return
      }

      setProgress(10)

      localStorage.setItem("tgUserId", getTgUserId())

      setProgress(20)

      const actions: Promise<any>[] = [
        dispatch(getMaintenance.initiate()),
        dispatch(getPlayerProfile.initiate()).unwrap().catch(clearAuthData),
        dispatch(fetchServerTime.initiate()),
        dispatch(getPlayerSettings.initiate())
        // dispatch(getPlayerInfo.initiate()),
        // dispatch(getPlayerStatistics.initiate()),
      ]

      const increment = 70 / actions.length

      await Promise.all(
        actions.map(async (action) =>
          updateProgressOnResolve(action, increment)
        )
      )

      setProgress(100)
      dispatch(setIsAuthorized(true))
    } catch (e) {
      const { statusCode, message } = parseAxiosError(e)

      if (statusCode === 403 && message === "User is not a labrador member") {
        dispatch(setIsLabrUser(false))
      }
    } finally {
      setTimeout(() => {
        dispatch(setIsLoaded(true))
      }, 300)
    }
  }

  useEffect(() => {
    bootstrap()
  }, [])

  return progress
}
